import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { ProgressButton } from '../../../common/ProgressButton';
import { RootState } from '../../../store';
import { validateEmail } from '../../../utils';
import { getUserGroups } from '../../usergroup/redux/userGroupSelectors';
import { UserGroup } from '../../usergroup/redux/userGroupSlices';
import { inviteExternalUser, openInviteExternalUserDialog } from '../redux/userSlices';
const mapStateToProps = (state: RootState) => ({
    groups: getUserGroups(state),
    userDialogModel: state.user.ui.inviteExternalUserDialog
});

type Props = ReturnType<typeof mapStateToProps>;

const InviteExternalUserDialog = ({ groups, userDialogModel }: Props) => {
    const [mail, setMail] = useState('');
    const [hasMailError, setHasMailError] = useState(false);
    const [selectedUserGroups, setSelectedUserGroups] = useState([] as number[]);

    const dispatch = useDispatch();

    useEffect(() => {
        setMail('');
        setHasMailError(false);
        setSelectedUserGroups([]);
    }, []);

    const groupsArr = useMemo(() => Object.values(groups), [groups]);

    const handleSetMail = (event: ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        setHasMailError(!validateEmail(email));
        setMail(email);
    };

    const handleSubmit = () => {
        if (hasMailError) {
            return;
        }

        dispatch(
            inviteExternalUser.request({
                email: mail,
                groupIdsToJoin: selectedUserGroups
            })
        );
    };

    const handleUserGroupsSelcted = (userGroups: UserGroup[]) => {
        setSelectedUserGroups(userGroups.map(ug => ug.userGroupId));
    };

    const handleClose = () => {
        dispatch(openInviteExternalUserDialog(false));
    };

    return (
        <Dialog
            open={userDialogModel.isOpen}
            onClose={handleClose}
            scroll="paper"
            //Todo: Check if paper needs this prop instead
            sx={{ minWidth: '500px', minHeight: '300px' }}
        >
            <DialogTitle>Invite external user</DialogTitle>
            <DialogContent dividers={true}>
                <Grid container direction="column" spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Email"
                            value={mail}
                            onChange={handleSetMail}
                            fullWidth={true}
                            autoFocus
                            error={hasMailError}
                            helperText={hasMailError ? 'Invalid email format' : ''}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete<UserGroup, true>
                            multiple
                            options={groupsArr}
                            getOptionLabel={option => option.name}
                            onChange={(_, value) => handleUserGroupsSelcted(value)}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    label="User groups"
                                    placeholder="User groups"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
                <ProgressButton
                    color="primary"
                    onClick={handleSubmit}
                    disabled={hasMailError}
                    isLoading={userDialogModel.isSaving}
                >
                    Invite
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(InviteExternalUserDialog);
